import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, DownloadSimple, Plus, Trash, Clock, SignIn, SignOut } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import ModalPortal from "@/components/ModalPortal";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDuration(seconds) {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function KindBadge({ kind }) {
  if (kind === "employee") {
    return <span className="tk-pill tk-pill-active text-[10px]">Anställd</span>;
  }
  return <span className="tk-pill tk-pill-warning text-[10px]">Underentr.</span>;
}

/**
 * Personalliggare-modal per objekt.
 * Visar aktiva stämplingar (anställda + gäster), historik, hantering av gäster
 * och PDF-export enligt Skatteverkets format.
 */
export default function PersonalliggareModal({ object, siteNumber, onClose }) {
  const [entries, setEntries] = useState({ active: [], history: [] });
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active"); // active | history | guests
  const [showGuestForm, setShowGuestForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, g] = await Promise.all([
        api.get(`/personalliggare/objects/${object.object_id}/entries`),
        api.get(`/personalliggare/objects/${object.object_id}/guests`),
      ]);
      setEntries(e.data || { active: [], history: [] });
      setGuests(g.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte hämta personalliggare");
    } finally {
      setLoading(false);
    }
  }, [object.object_id]);

  useEffect(() => { load(); }, [load]);

  const clockGuest = async (guest, action) => {
    try {
      await api.post(
        `/personalliggare/objects/${object.object_id}/guests/${guest.guest_id}/${action}`
      );
      toast.success(action === "clock-in" ? `${guest.name} instämplad` : `${guest.name} utstämplad`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fel");
    }
  };

  const deleteGuest = async (guest) => {
    if (!window.confirm(`Radera gäst ${guest.name}? Alla stämplingar för gästen tas bort.`)) return;
    try {
      await api.delete(`/personalliggare/objects/${object.object_id}/guests/${guest.guest_id}`);
      toast.success("Gäst raderad");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte radera");
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/personalliggare/objects/${object.object_id}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const filename = `personalliggare-${object.name || "objekt"}.pdf`.replace(/[^\w\-\.]+/g, "_");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF-export misslyckades");
    }
  };

  return (
    <ModalPortal>
      <div
        data-testid="personalliggare-modal"
        className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-surface border border-app rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-app flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-text-muted">Personalliggare</div>
              <h2 className="font-heading text-2xl md:text-3xl truncate">{object.name}</h2>
              <div className="text-xs text-text-muted mt-1">
                {object.address || "Ingen adress"}
                {siteNumber ? <> · ID06 arbetsplats: <span className="font-mono">{siteNumber}</span></> : null}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid="download-personalliggare-pdf"
                onClick={downloadPdf}
                className="tk-btn-primary inline-flex items-center gap-2"
              >
                <DownloadSimple size={16} /> PDF
              </button>
              <button
                onClick={onClose}
                className="text-text-muted hover:text-text-primary p-2"
                aria-label="Stäng"
              >
                <X size={22} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-4 border-b border-app flex gap-1">
            {[
              { key: "active", label: `Aktiva (${entries.active.length})` },
              { key: "history", label: `Historik (${entries.history.length})` },
              { key: "guests", label: `Underentreprenörer (${guests.length})` },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`personalliggare-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                  tab === t.key
                    ? "border-[var(--brand-rust)] text-text-primary font-medium"
                    : "border-transparent text-text-muted hover:text-text-primary"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <p className="text-center text-text-muted py-12 text-sm">Laddar…</p>
            ) : tab === "active" ? (
              <EntriesTable rows={entries.active} showEnd={false} emptyText="Inga instämplade just nu." testId="active-entries" />
            ) : tab === "history" ? (
              <EntriesTable rows={entries.history} showEnd={true} emptyText="Ingen historik ännu." testId="history-entries" />
            ) : (
              <GuestList
                guests={guests}
                activeGuestIds={new Set(entries.active.filter((r) => r.kind === "guest").map((r) => r.guest_id))}
                onClock={clockGuest}
                onDelete={deleteGuest}
                onShowForm={() => setShowGuestForm(true)}
              />
            )}
          </div>
        </div>
      </div>

      {showGuestForm && (
        <GuestFormModal
          objectId={object.object_id}
          onClose={() => setShowGuestForm(false)}
          onSaved={() => { setShowGuestForm(false); load(); }}
        />
      )}
    </ModalPortal>
  );
}

function EntriesTable({ rows, showEnd, emptyText, testId }) {
  if (!rows || rows.length === 0) {
    return <p className="text-center text-text-muted py-10 text-sm" data-testid={`${testId}-empty`}>{emptyText}</p>;
  }
  return (
    <div className="tk-card p-0 overflow-hidden" data-testid={testId}>
      <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-text-muted border-b border-app">
        <div className="col-span-3">Person</div>
        <div className="col-span-2">Personnummer</div>
        <div className="col-span-2">Företag</div>
        <div className="col-span-1">ID06</div>
        <div className="col-span-2">In</div>
        <div className="col-span-2">{showEnd ? "Ut" : "Status"}</div>
      </div>
      {rows.map((r, i) => (
        <div
          key={r.entry_id || i}
          data-testid={`${testId}-row-${i}`}
          className="grid grid-cols-12 px-4 py-3 border-b border-app last:border-0 items-center text-sm gap-y-1"
        >
          <div className="col-span-12 md:col-span-3 flex items-center gap-2 min-w-0">
            <KindBadge kind={r.kind} />
            <span className="truncate font-medium">{r.person_name}</span>
          </div>
          <div className="col-span-6 md:col-span-2 font-mono text-xs">
            {r.has_personnummer ? r.personnummer_masked : <span className="text-text-muted">saknas</span>}
          </div>
          <div className="col-span-6 md:col-span-2 text-xs text-text-muted truncate">{r.company_name || "—"}</div>
          <div className="col-span-6 md:col-span-1 font-mono text-xs">{r.id06_number || "—"}</div>
          <div className="col-span-6 md:col-span-2 text-xs tabular-nums">{fmtDateTime(r.start_time)}</div>
          <div className="col-span-12 md:col-span-2 text-xs tabular-nums">
            {showEnd ? (
              <>
                {fmtDateTime(r.end_time)}
                {r.duration_seconds ? <span className="text-text-muted ml-2">({fmtDuration(r.duration_seconds)})</span> : null}
              </>
            ) : (
              <span className="tk-pill tk-pill-active text-[10px] inline-flex items-center gap-1">
                <Clock size={10} /> Pågår
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function GuestList({ guests, activeGuestIds, onClock, onDelete, onShowForm }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-muted">
          Lägg till underentreprenörer manuellt. Stämpla in/ut dem när de kommer och går från arbetsplatsen.
        </p>
        <button
          data-testid="add-guest-button"
          onClick={onShowForm}
          className="tk-btn-primary inline-flex items-center gap-2"
        >
          <Plus size={16} /> Lägg till gäst
        </button>
      </div>

      {guests.length === 0 ? (
        <p className="text-center text-text-muted py-10 text-sm" data-testid="guests-empty">
          Inga underentreprenörer tillagda ännu.
        </p>
      ) : (
        <div className="tk-card p-0 overflow-hidden" data-testid="guests-list">
          {guests.map((g) => {
            const isActive = activeGuestIds.has(g.guest_id);
            return (
              <div
                key={g.guest_id}
                data-testid={`guest-row-${g.guest_id}`}
                className="grid grid-cols-12 px-4 py-3 border-b border-app last:border-0 items-center gap-y-2"
              >
                <div className="col-span-12 md:col-span-4">
                  <div className="font-heading">{g.name}</div>
                  <div className="text-xs text-text-muted">
                    <span className="font-mono">{g.personnummer_masked || "personnr saknas"}</span>
                    {g.id06_number ? <> · ID06 <span className="font-mono">{g.id06_number}</span></> : null}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-4 text-sm text-text-muted">
                  {g.company_name || "—"}
                  {g.role_or_trade ? <> · {g.role_or_trade}</> : null}
                </div>
                <div className="col-span-12 md:col-span-4 flex justify-end gap-2 flex-wrap">
                  {isActive ? (
                    <button
                      data-testid={`clock-out-guest-${g.guest_id}`}
                      onClick={() => onClock(g, "clock-out")}
                      className="tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1"
                    >
                      <SignOut size={14} /> Stämpla ut
                    </button>
                  ) : (
                    <button
                      data-testid={`clock-in-guest-${g.guest_id}`}
                      onClick={() => onClock(g, "clock-in")}
                      className="tk-btn-primary !py-2 !px-3 text-xs inline-flex items-center gap-1"
                    >
                      <SignIn size={14} /> Stämpla in
                    </button>
                  )}
                  <button
                    data-testid={`delete-guest-${g.guest_id}`}
                    onClick={() => onDelete(g)}
                    className="tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1 text-rust"
                  >
                    <Trash size={14} /> Radera
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GuestFormModal({ objectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    personnummer: "",
    company_name: "",
    role_or_trade: "",
    id06_number: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/personalliggare/objects/${objectId}/guests`, {
        name: form.name.trim(),
        personnummer: form.personnummer.trim(),
        company_name: form.company_name.trim() || null,
        role_or_trade: form.role_or_trade.trim() || null,
        id06_number: form.id06_number.trim() || null,
        note: form.note.trim() || null,
      });
      toast.success("Gäst tillagd");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte lägga till");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <div
        data-testid="guest-form-modal"
        className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
        onClick={() => !saving && onClose()}
      >
        <div
          className="bg-surface border border-app rounded-2xl p-6 w-full max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl">Lägg till underentreprenör</h2>
            <button onClick={() => !saving && onClose()} className="text-text-muted hover:text-text-primary" aria-label="Stäng">
              <X size={20} />
            </button>
          </div>
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div>
              <label className="text-xs text-text-muted">Namn *</label>
              <input
                data-testid="guest-name-input"
                required minLength={2} maxLength={80}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="tk-input w-full"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted">Personnummer *</label>
              <input
                data-testid="guest-personnummer-input"
                required
                placeholder="ÅÅÅÅMMDD-NNNN"
                value={form.personnummer}
                onChange={(e) => setForm({ ...form, personnummer: e.target.value })}
                className="tk-input w-full font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-text-muted">Företag</label>
                <input
                  data-testid="guest-company-input"
                  maxLength={80}
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="tk-input w-full"
                />
              </div>
              <div>
                <label className="text-xs text-text-muted">Yrke / Roll</label>
                <input
                  data-testid="guest-role-input"
                  maxLength={60}
                  value={form.role_or_trade}
                  onChange={(e) => setForm({ ...form, role_or_trade: e.target.value })}
                  className="tk-input w-full"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-text-muted">ID06-nummer (valfritt)</label>
              <input
                data-testid="guest-id06-input"
                maxLength={40}
                value={form.id06_number}
                onChange={(e) => setForm({ ...form, id06_number: e.target.value })}
                className="tk-input w-full font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted">Anteckning (valfritt)</label>
              <input
                data-testid="guest-note-input"
                maxLength={200}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="tk-input w-full"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} disabled={saving} className="tk-btn-outline flex-1">
                Avbryt
              </button>
              <button
                type="submit"
                data-testid="save-guest-button"
                disabled={saving || !form.name.trim() || !form.personnummer.trim()}
                className="tk-btn-primary flex-1"
              >
                {saving ? "Sparar…" : "Spara gäst"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

// Keep API import consumed for downstream tooling
export { API };
